import { useParams } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import * as groupsApi from '../../../services/api/groupsApi';
import { getGroupDetailById } from '../../../data/mockGroupDetailData';
import { GroupDetail } from '../../../types/groupDetail';

export const useGroupDetailData = () => {
  const { id } = useParams<{ id: string }>();
  
  const [groupData, setGroupData] = useState<GroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch detailed group from API with progressive loading
  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchGroup = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log(`[useGroupDetailData] Fetching group profile (first page) for ID: ${id}`);
        
        // Step 1: Fast initial load with first page of members only
        const profileData = await groupsApi.getGroupProfileFirstPage(id);
        
        if (isMounted) {
          // Data is already formatted by BFF, cast to GroupDetail
          setGroupData(profileData as unknown as GroupDetail);
          setLoading(false);
          
          const typedProfile = profileData as any;
          console.log(`[useGroupDetailData] ✓ Group profile (first page) loaded for: ${typedProfile.name || 'Unknown'}`);
          console.log(`[useGroupDetailData] Initial member count: ${typedProfile.members?.length || 0}`);
          
          // Step 2: If there are more members, fetch them in the background
          if (typedProfile.hasMore) {
            console.log(`[useGroupDetailData] More members available, fetching remaining pages...`);
            setLoadingMore(true);
            
            try {
              const additionalMembers = await groupsApi.getGroupMembersRemainingPages(id, 2);
              
              if (isMounted) {
                // Merge additional members with existing ones
                setGroupData(prev => {
                  if (!prev) return prev;
                  
                  const allMembers = [
                    ...(prev.members || []),
                    ...(additionalMembers.members || [])
                  ];
                  
                  console.log(`[useGroupDetailData] ✓ All members loaded. Total: ${allMembers.length}`);
                  
                  return {
                    ...prev,
                    members: allMembers,
                    hasMore: false,
                  };
                });
              }
            } catch (moreErr) {
              console.warn('[useGroupDetailData] Failed to fetch remaining members:', moreErr);
              // Don't fail the whole page if background loading fails
            } finally {
              if (isMounted) {
                setLoadingMore(false);
              }
            }
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('[useGroupDetailData] Error fetching group profile:', err);
          
          // Only use mock data for authentication errors
          const isAuthError = err && typeof err === 'object' && 'response' in err && 
            (err.response as any)?.status && 
            ((err.response as any).status === 401 || (err.response as any).status === 403);
          
          if (isAuthError) {
            console.log('[useGroupDetailData] Authentication error - using mock data for development');
            const mockDetail = getGroupDetailById(id);
            if (mockDetail) {
              setGroupData(mockDetail);
            } else {
              setError(err instanceof Error ? err : new Error('Authentication failed'));
            }
          } else {
            // For other errors, set error state
            setError(err instanceof Error ? err : new Error('Failed to fetch group profile'));
          }
          setLoading(false);
        }
      }
    };

    fetchGroup();

    return () => {
      isMounted = false;
    };
  }, [id]);

  // Sort members alphabetically by name
  const sortedMembers = useMemo(() => 
    groupData?.members?.slice().sort((a, b) => 
      a.name.localeCompare(b.name)
    ) || [],
    [groupData]
  );

  return { 
    groupDetail: groupData,
    sortedMembers,
    loading,
    loadingMore,
    error,
    isFromApi: !!groupData,
  };
};
